// CAPETELE UNUI ESV — piesele lui, fețele care se trag cu mâna și unirea a
// două elemente care ajung să se atingă.
//
// PIESELE. Pe ecran un ESV e făcut din dreptunghiuri care NU se suprapun:
// piciorul, cu tot betonul îmbinărilor lui (colțul unui L e al piciorului),
// și brațele, fiecare pornit din fața piciorului, de o parte a lui. Un L sunt
// două dreptunghiuri, un T trei capete, o cruce patru. Schema (`brate`, v.
// `editorForme`) lasă brațul să treacă prin picior — amprenta e reuniunea, iar
// serverul ia drept braț numai ce trece de latura minimă (`tipuri.bratele`) —,
// deci un braț de amândouă părțile sunt două piese, iar unul de o singură
// parte pornește, în date, din fața de DEPARTE a piciorului.
//
// CAPĂTUL LIBER al unei piese e fața de la capătul ei pe care nu stă nimic:
// capetele piciorului fără braț lipit de ele și vârfurile brațelor. Tras cu
// mâna, numai el se mișcă; fața de la celălalt capăt rămâne. Piciorul nu scade
// sub latura minimă și nu lasă un braț fără reazem; brațul tras până în fața
// piciorului dispare, iar «+»-ul de lângă zid îl reface. Capătul piciorului pe
// care stă un braț e un COLȚ: nu se trage, iar «+»-ul de acolo lungește
// piciorul pe zid, peste braț (`editorForme.continuari`).
//
// LA ATINGERE SE UNESC. Fața trasă se oprește în fața următorului ESV de pe
// drum; lăsată acolo, cele două devin un singur element, iar grinda dintre ele
// pleacă (`unesteEsv`). Schema are un singur picior și brațe numai pe cealaltă
// axă, deci se unesc doar ce încape în ea: două picioare în prelungire, un
// element fără brațe care devine brațul celuilalt, un braț prelungit peste un
// element fără brațe.
//
// REGULA VERTICALĂ. O secțiune poate scădea în sus, niciodată crește (nici prin
// brațe — `tipuri.valideaza`). Pe un singur nivel, o creștere se face și pe
// nivelurile de DEDESUBT (cel puțin cât sus), o scădere și pe cele de DEASUPRA
// (cel mult cât jos).
//
// Coordonatele sunt milimetrii planșei, în cadrul clădirii, unde zidurile și
// elementele stau pe X și Y.

import {
  amprentaEsv, axelePeretilorMm, capatulGrinzii, LATURA_MIN_CM, nivelurileEsv,
  nivelurileGrinzii, sectiunea, type Brat, type Esv, type Geometrie, type Grinda,
  type NivelContext, type Pt, type SectiuneEsv,
} from "./editorGeometrie";

/** Toleranța de contact: rotunjirile, nu o hotărâre. */
const EPS = 0.5;
/** Cât de aproape de fața piesei trebuie să stea capătul unei grinzi ca să
 *  fie «prins de față» și s-o urmeze. */
const TOL_FATA_MM = 5;
/** Pasul lungimii unei piese trase fără nimic de care să se lipească. */
export const PAS_LUNGIME_MM = 50;
/** Sub atât o piesă de braț nu se toarnă: brațul dispare. */
export const BRAT_MIN_MM = 50;

const r1 = (v: number) => Math.round(v * 10) / 10;

/** O față de capăt care se trage. */
export type Capat = {
  /** `null` = piciorul; altfel indexul brațului în `brate` */
  brat: number | null;
  /** locul brațului de-a lungul piciorului, față de nod (numai la braț): după
   *  el se recunoaște brațul pe celelalte niveluri */
  la_mm?: number;
  /** încotro iese fața: capătul piciorului (±1 pe axa lui) sau partea
   *  brațului (±1 pe axa brațului) */
  sens: 1 | -1;
  /** axa planșei pe care se mișcă fața (0 = x, 1 = y) */
  axa: 0 | 1;
  /** unde stă fața pe axa ei */
  poz: number;
  /** de unde se măsoară piesa: capătul celălalt al piciorului, sau fața
   *  piciorului din care pleacă brațul */
  fix: number;
  /** întinderea feței pe cealaltă axă */
  peste: [number, number];
};

/** Piesele ESV-ului la un nivel: dreptunghiurile de desenat, capetele libere,
 *  capetele piciorului care sunt colțuri și întinderea piciorului. */
export type Piese = {
  dreptunghiuri: Pt[][];
  capete: Capat[];
  colturi: (1 | -1)[];
  /** piciorul cu colțurile lui, pe axa lui: `[de, la]` */
  picior: [number, number];
};

/** Indicii axelor: `lg` de-a lungul piciorului, `tr` de-a curmezișul. */
export const indici = (e: Esv): { lg: 0 | 1; tr: 0 | 1 } =>
  (e.axa === "X" ? { lg: 0, tr: 1 } : { lg: 1, tr: 0 });

/** O amprentă purtată din Pasul 4 (`dim_extra.poly_mm`, relativă la nod) care
 *  nu e piciorul: un L/T fără brațe declarate. Desenul o arată întreagă, iar
 *  capetele ei nu se trag. Un dreptunghi alunecat — cel mai des — e chiar
 *  piciorul: Pasul 4 îl poartă ca amprentă numai fiindcă e descentrat. */
export function arePoligon(e: Esv, s: SectiuneEsv | undefined): boolean {
  const poly = s?.dim_extra?.poly_mm as Pt[] | undefined;
  if (!s || !poly || poly.length < 3) return false;
  if (poly.length !== 4) return true;
  const { lg } = indici(e);
  const o = s.ofs_mm ?? e.ofs_mm ?? 0;
  const [l0, l1, t0, t1] = [o - s.L_cm * 5, o + s.L_cm * 5, -s.b_cm * 5, s.b_cm * 5];
  const picior = lg === 0 ? [l0, t0, l1, t1] : [t0, l0, t1, l1];
  const xs = poly.map((q) => q[0]);
  const ys = poly.map((q) => q[1]);
  const cutie = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  let aria = 0;
  poly.forEach((q, i) => {
    const r = poly[(i + 1) % poly.length];
    aria += q[0] * r[1] - r[0] * q[1];
  });
  const dreptunghi = Math.abs(Math.abs(aria) / 2 - (cutie[2] - cutie[0]) * (cutie[3] - cutie[1]))
    <= 0.01 * Math.abs(aria) / 2 + 1;
  return !dreptunghi || cutie.some((v, i) => Math.abs(v - picior[i]) > 1.5);
}

type BratAbs = { index: number; la_mm: number; b_cm: number;
                 l0: number; l1: number; t0: number; t1: number };
/** Secțiunea unui nivel în coordonate absolute: piciorul din date (`a0`,
 *  `a1`), cel desenat, cu colțurile (`A0`, `A1`), banda lui (`bLo`, `bHi`) și
 *  brațele. */
type Forma = {
  lg: 0 | 1; tr: 0 | 1;
  a0: number; a1: number; A0: number; A1: number; bLo: number; bHi: number;
  brate: BratAbs[];
};

/** Brațul taie banda piciorului (stă lipit de el, de-a curmezișul). */
const taie = (f: Pick<Forma, "bLo" | "bHi">, x: BratAbs) =>
  x.t0 < f.bHi - EPS && x.t1 > f.bLo + EPS;

function forma(e: Esv, s: SectiuneEsv): Forma {
  const { lg, tr } = indici(e);
  const o = s.ofs_mm ?? e.ofs_mm ?? 0;
  const L = s.L_cm * 10;
  const b = s.b_cm * 10;
  const a0 = e.nod[lg] + o - L / 2;
  const a1 = e.nod[lg] + o + L / 2;
  const bLo = e.nod[tr] - b / 2;
  const bHi = e.nod[tr] + b / 2;
  // ca `amprentaEsv`: un braț cât latura minimă nu se desenează
  const brate: BratAbs[] = (s.brate ?? []).flatMap((x, index) => (x.L_cm <= LATURA_MIN_CM + 1e-6 ? []
    : [{ index, la_mm: x.la_mm, b_cm: x.b_cm,
         l0: e.nod[lg] + x.la_mm - x.b_cm * 5, l1: e.nod[lg] + x.la_mm + x.b_cm * 5,
         t0: e.nod[tr] + x.ofs_mm - x.L_cm * 5, t1: e.nod[tr] + x.ofs_mm + x.L_cm * 5 }]));
  // piciorul cuprinde betonul brațelor care îl taie: colțul e al lui
  let A0 = a0, A1 = a1;
  for (const x of brate) {
    if (taie({ bLo, bHi }, x) && x.l0 <= a1 + EPS && x.l1 >= a0 - EPS) {
      A0 = Math.min(A0, x.l0);
      A1 = Math.max(A1, x.l1);
    }
  }
  return { lg, tr, a0, a1, A0, A1, bLo, bHi, brate };
}

/** Cât acoperă brațele piciorului spre capătul `sens`: fața piciorului nu
 *  poate intra mai mult de atât fără să lase un braț în aer. */
function acoperireaBratelor(f: Forma, sens: 1 | -1): number | null {
  const lipite = f.brate.filter((x) => taie(f, x));
  if (!lipite.length) return null;
  return sens > 0 ? Math.max(...lipite.map((x) => x.l1)) : Math.min(...lipite.map((x) => x.l0));
}

/** PIESELE ESV-ULUI LA `nivel` — `null` fără secțiune, sau cu o amprentă
 *  purtată din Pasul 4. */
export function piesele(e: Esv, nivel: number): Piese | null {
  const s = sectiunea(e, nivel);
  if (!s || arePoligon(e, s)) return null;
  const f = forma(e, s);
  const pt = (a: number, t: number): Pt => (f.lg === 0 ? [a, t] : [t, a]);
  const rect = (a0: number, a1: number, t0: number, t1: number): Pt[] =>
    [pt(a0, t0), pt(a1, t0), pt(a1, t1), pt(a0, t1)];
  const dreptunghiuri = [rect(f.A0, f.A1, f.bLo, f.bHi)];
  const capete: Capat[] = [];
  const colturi: (1 | -1)[] = [];
  for (const sens of [-1, 1] as const) {
    const fata = sens > 0 ? f.A1 : f.A0;
    const colt = f.brate.some((x) => taie(f, x)
      && (sens > 0 ? x.l1 >= f.A1 - EPS : x.l0 <= f.A0 + EPS));
    if (colt) colturi.push(sens);
    else capete.push({ brat: null, sens, axa: f.lg, poz: fata, fix: sens > 0 ? f.A0 : f.A1,
                       peste: [f.bLo, f.bHi] });
  }
  for (const x of f.brate) {
    for (const parte of [-1, 1] as const) {
      const varf = parte > 0 ? x.t1 : x.t0;
      const fata = parte > 0 ? f.bHi : f.bLo;
      if ((varf - fata) * parte <= EPS) continue;          // nu iese pe partea asta
      // piesa pornește din fața piciorului; un braț desprins de bandă, din
      // capătul lui
      const baza = parte > 0 ? Math.max(x.t0, f.bHi) : Math.min(x.t1, f.bLo);
      dreptunghiuri.push(parte > 0 ? rect(x.l0, x.l1, baza, varf) : rect(x.l0, x.l1, varf, baza));
      capete.push({ brat: x.index, la_mm: x.la_mm, sens: parte, axa: f.tr, poz: varf, fix: baza,
                    peste: [x.l0, x.l1] });
    }
  }
  return { dreptunghiuri, capete, colturi, picior: [f.A0, f.A1] };
}

/** Conturul de desenat: piesele, sau amprenta întreagă când elementul nu se
 *  poate citi ca picior cu brațe. */
export const conturulEsv = (e: Esv, nivel: number): Pt[][] =>
  piesele(e, nivel)?.dreptunghiuri ?? amprentaEsv(e, nivel);

// --- unde ajunge fața trasă ---------------------------------------------------

/** Unde a ajuns fața: poziția, ESV-ul în care s-a oprit (lipită de el) și
 *  linia de care s-a lipit, de desenat. */
export type Ajuns = { poz: number; vecin: string | null; ghidaj: number | null };

/** UNDE CADE FAȚA `c` TRASĂ SPRE `dorit` (mm pe axa ei), la `nivel`.
 *
 *    1. se lipește, la mai puțin de `tol_mm`, de capetele zidului în care stă
 *       piesa, de fețele zidurilor care îl taie, de marginile golurilor și de
 *       fețele paralele ale celorlalte ESV-uri, oricât de departe (fața trasă
 *       ajunge în linie cu ele, ca o grindă să încapă drept între cele două)
 *       — ori se întoarce unde era; altfel lungimea piesei cade pe
 *       `PAS_LUNGIME_MM`;
 *    2. piciorul nu scade sub latura minimă și nu lasă un braț fără reazem;
 *       brațul scade până la zero (sub `BRAT_MIN_MM` dispare);
 *    3. nu trece de fața următorului ESV de pe drum: acolo se oprește, lipită
 *       de el (`vecin`). */
export function undeAjunge(G: Geometrie, e: Esv, nivel: number, c: Capat, dorit: number,
                           n: NivelContext | null, tol_mm: number): Ajuns {
  const k = c.axa;
  const q: 0 | 1 = k === 0 ? 1 : 0;
  const lung = (p: number) => (p - c.fix) * c.sens;
  const la = (u: number) => c.fix + c.sens * u;
  const u0 = lung(c.poz);

  // înăuntru
  let uMin = 0;
  const s = sectiunea(e, nivel);
  if (c.brat == null && s) {
    const f = forma(e, s);
    uMin = LATURA_MIN_CM * 10;
    const ac = acoperireaBratelor(f, c.sens);
    if (ac != null) uMin = Math.max(uMin, lung(ac));
  }
  // în afară: până la următorul ESV de pe drum
  let uMax = Infinity;
  let vecin: string | null = null;
  for (const x of G.esv) {
    if (x.id === e.id) continue;
    for (const pts of amprentaEsv(x, nivel)) {
      const pq = pts.map((v) => v[q]);
      const pk = pts.map((v) => v[k]);
      if (Math.min(Math.max(...pq), c.peste[1]) - Math.max(Math.min(...pq), c.peste[0]) <= EPS) continue;
      const u = lung(c.sens > 0 ? Math.min(...pk) : Math.max(...pk));
      if (u < u0 - EPS) continue;                 // în spate, sau deja suprapus
      if (u < uMax) { uMax = u; vecin = x.id; }
    }
  }

  // lipirea
  const cand: number[] = [c.poz];
  if (c.brat != null) cand.push(c.fix);
  if (n) {
    const linie = (c.peste[0] + c.peste[1]) / 2;
    const dirAxa = k === 0 ? "X" : "Y";
    for (const w of axelePeretilorMm(n)) {
      if (w.axa === dirAxa) {
        if (Math.abs(linie - w.cota) <= w.grosime / 2 + 1) cand.push(w.de, w.la);
      } else if (linie >= w.de - 1 && linie <= w.la + 1) {
        cand.push(w.cota - w.grosime / 2, w.cota + w.grosime / 2);
      }
    }
    for (const g of n.goluri) {
      if (g.colturi.length < 3) continue;
      const gq = g.colturi.map((v) => v[q]);
      const gk = g.colturi.map((v) => v[k]);
      if (linie >= Math.min(...gq) - 1 && linie <= Math.max(...gq) + 1)
        cand.push(Math.min(...gk), Math.max(...gk));
    }
  }
  for (const x of G.esv) {
    if (x.id === e.id) continue;
    for (const pts of amprentaEsv(x, nivel)) {
      pts.forEach((v, i) => {
        const w = pts[(i + 1) % pts.length];
        if (Math.abs(v[k] - w[k]) <= EPS && Math.abs(v[q] - w[q]) > EPS) cand.push(v[k]);
      });
    }
  }
  let ghidaj: number | null = null;
  let best = tol_mm;
  for (const x of cand) {
    const d = Math.abs(x - dorit);
    if (d <= best) { best = d; ghidaj = x; }
  }
  let u = ghidaj != null ? lung(ghidaj)
    : Math.round(lung(dorit) / PAS_LUNGIME_MM) * PAS_LUNGIME_MM;
  if (c.brat != null && u < BRAT_MIN_MM) u = 0;
  if (u < uMin) { u = uMin; ghidaj = null; }
  if (u >= uMax - EPS) return { poz: la(uMax), vecin, ghidaj: la(uMax) };
  if (ghidaj != null && Math.abs(la(u) - ghidaj) > EPS) ghidaj = null;
  return { poz: la(u), vecin: null, ghidaj };
}

// --- tragerea -------------------------------------------------------------------

/** Secțiunea fără amprenta purtată din Pasul 4: ea descria vechea formă. */
function faraAmprenta(s: SectiuneEsv): SectiuneEsv {
  const { dim_extra: _x, ...rest } = s;
  void _x;
  return rest;
}

/** Piciorul secțiunii, scris cât cel desenat: cu colțurile brațelor care îl
 *  taie. Reuniunea nu se schimbă, dar lungimea piciorului spune atunci ce se
 *  vede, pe toate nivelurile la fel — altfel un nivel retușat ar ieși mai lung
 *  decât cel de sub el. */
function cuPiciorulIntreg(e: Esv, s: SectiuneEsv): SectiuneEsv {
  if (arePoligon(e, s)) return s;
  const f = forma(e, s);
  if (Math.abs(f.A0 - f.a0) < 0.05 && Math.abs(f.A1 - f.a1) < 0.05) return s;
  return faraAmprenta({ ...s, L_cm: r1((f.A1 - f.A0) / 10),
                        ofs_mm: r1((f.A0 + f.A1) / 2 - e.nod[f.lg]) });
}

function normalizat(e: Esv): Esv {
  return { ...e, sectiune_per_nivel: Object.fromEntries(Object.entries(e.sectiune_per_nivel)
    .map(([k, s]) => [k, cuPiciorulIntreg(e, s)])) };
}

/** Pe nivelul ăsta fața merge exact la `poz`, dedesubt cel puțin până acolo,
 *  deasupra cel mult. */
type Cat = "exact" | "cel_putin" | "cel_mult";
const pana = (acum: number, poz: number, sens: 1 | -1, cat: Cat) =>
  cat === "exact" ? poz
    : (cat === "cel_putin") === (sens > 0) ? Math.max(acum, poz) : Math.min(acum, poz);

function cuPiciorul(e: Esv, s: SectiuneEsv, sens: 1 | -1, poz: number, cat: Cat): SectiuneEsv {
  const f = forma(e, s);
  const fix = sens > 0 ? f.A0 : f.A1;
  let fata = pana(sens > 0 ? f.A1 : f.A0, poz, sens, cat);
  // nu sub latura minimă, nu lăsând un braț fără reazem
  const limite = [fix + sens * LATURA_MIN_CM * 10, acoperireaBratelor(f, sens) ?? fix];
  fata = sens > 0 ? Math.max(fata, ...limite) : Math.min(fata, ...limite);
  const [lo, hi] = sens > 0 ? [fix, fata] : [fata, fix];
  return faraAmprenta({ ...s, L_cm: r1((hi - lo) / 10), ofs_mm: r1((lo + hi) / 2 - e.nod[f.lg]) });
}

function cuBratul(e: Esv, s: SectiuneEsv, ref: Brat, parte: 1 | -1, poz: number,
                  cat: Cat, creste: boolean): SectiuneEsv {
  const f = forma(e, s);
  const lista = s.brate ?? [];
  const i = lista.findIndex((x) => Math.abs(x.la_mm - ref.la_mm) <= 1);
  const fata = parte > 0 ? f.bHi : f.bLo;            // fața din care pleacă piesa
  const departe = parte > 0 ? f.bLo : f.bHi;          // fața cealaltă a piciorului
  let t0: number, t1: number, b_cm: number;
  if (i < 0) {
    // brațul lipsește aici: la o creștere vine întreg, ca sus să nu fie mai
    // mult beton decât jos; o scurtare n-are ce scurta
    if (!creste || (poz - fata) * parte <= EPS) return s;
    [t0, t1] = parte > 0 ? [departe, poz] : [poz, departe];
    b_cm = ref.b_cm;
  } else {
    const x = lista[i];
    t0 = e.nod[f.tr] + x.ofs_mm - x.L_cm * 5;
    t1 = e.nod[f.tr] + x.ofs_mm + x.L_cm * 5;
    b_cm = x.b_cm;
    const varf = pana(parte > 0 ? t1 : t0, poz, parte, cat);
    // partea cealaltă: rămâne, dacă iese din picior; altfel brațul pornește
    // din fața de departe a piciorului
    const alt = parte > 0 ? t0 : t1;
    const altIese = (departe - alt) * parte > EPS;
    const altul = altIese ? alt : (parte > 0 ? Math.min(alt, departe) : Math.max(alt, departe));
    if ((varf - fata) * parte <= EPS) {
      // piesa a ajuns în fața piciorului: dispare; brațul rămâne numai dacă
      // iese de partea cealaltă
      if (!altIese) {
        const brate = lista.filter((_, j) => j !== i);
        const { brate: _b, ...rest } = s;
        void _b;
        return faraAmprenta(brate.length ? { ...rest, brate } : rest);
      }
      [t0, t1] = parte > 0 ? [altul, fata] : [fata, altul];
    } else {
      [t0, t1] = parte > 0 ? [altul, varf] : [varf, altul];
    }
  }
  const nou: Brat = { la_mm: ref.la_mm, b_cm, L_cm: r1((t1 - t0) / 10),
                      ofs_mm: r1((t0 + t1) / 2 - e.nod[f.tr]) };
  const brate = i < 0 ? [...lista, nou] : lista.map((x, j) => (j === i ? { ...x, ...nou, la_mm: x.la_mm } : x));
  // piciorul cuprinde brațul pe lățimea lui (un braț adus pe un nivel de
  // dedesubt unde piciorul era mai scurt)
  return cuPiciorulIntreg(e, faraAmprenta({ ...s, brate }));
}

/** GRINZILE PRINSE DE FAȚA TRASĂ O URMEAZĂ. Capătul unei grinzi care stă pe
 *  fața de la capătul piesei și pleacă din ea în lungul piesei rămâne pe față:
 *  pe toate nivelurile grinzii, deci cel mult unde ajunge fața cea mai
 *  retrasă dintre ele (acolo e beton pe toate). Celelalte capete își caută
 *  linia după (`aseazaGrinzile`). */
function grinzileUrmeaza(G: Geometrie, vechi: Geometrie, id: string, c: Capat): Geometrie {
  const e = G.esv.find((x) => x.id === id);
  if (!e) return G;
  const k = c.axa;
  const q: 0 | 1 = k === 0 ? 1 : 0;
  const fataLa = (n: number): number | null => {
    const s = sectiunea(e, n);
    if (!s || arePoligon(e, s)) return null;
    const f = forma(e, s);
    if (c.brat == null) return c.sens > 0 ? f.A1 : f.A0;
    const fp = c.sens > 0 ? f.bHi : f.bLo;
    const x = f.brate.find((y) => Math.abs(y.la_mm - (c.la_mm ?? 0)) <= 1);
    const v = x ? (c.sens > 0 ? x.t1 : x.t0) : fp;
    return (v - fp) * c.sens > 0 ? v : fp;
  };
  let schimbat = false;
  const grinzi = G.grinzi.map((g) => {
    let out: Grinda = g;
    for (const cap of ["i", "j"] as const) {
      if ((cap === "i" ? g.nod_i : g.nod_j) !== id) continue;
      const P = capatulGrinzii(vechi, g, cap);
      const Q = capatulGrinzii(vechi, g, cap === "i" ? "j" : "i");
      if (!P || !Q) continue;
      if (Math.abs(P[k] - c.poz) > TOL_FATA_MM) continue;
      if (P[q] < c.peste[0] - 1 || P[q] > c.peste[1] + 1) continue;
      if (Math.abs(Q[k] - P[k]) <= Math.abs(Q[q] - P[q])) continue;   // nu e în lungul piesei
      if ((Q[k] - P[k]) * c.sens <= 0) continue;                        // nu pleacă din față
      const fete = nivelurileGrinzii(g).map(fataLa).filter((v): v is number => v != null);
      if (!fete.length) continue;
      const fNou = c.sens > 0 ? Math.min(...fete) : Math.max(...fete);
      if (Math.abs(fNou - P[k]) < 0.05) continue;
      const o: Pt = [P[0] - e.nod[0], P[1] - e.nod[1]];
      o[k] = fNou - e.nod[k];
      const curat = Math.abs(o[0]) < 1e-6 && Math.abs(o[1]) < 1e-6 ? null : o;
      out = cap === "i" ? { ...out, ofs_i: curat } : { ...out, ofs_j: curat };
      schimbat = true;
    }
    return out;
  });
  return schimbat ? { ...G, grinzi } : G;
}

/** FAȚA `c` A ESV-ULUI `id` (citită la `nivelRef`) MUTATĂ LA `poz`: la
 *  `nivel` după regula verticală — o creștere și dedesubt, o scădere și
 *  deasupra —, sau pe toate nivelurile (`nivel == null`). Grinzile prinse de
 *  față o urmează; celelalte capete rămân de așezat (`aseazaGrinzile`). */
export function trageCapatul(G: Geometrie, id: string, nivelRef: number, nivel: number | null,
                             c: Capat, poz: number): Geometrie {
  const e0 = G.esv.find((x) => x.id === id);
  const ref0 = e0 && sectiunea(e0, nivelRef);
  if (!e0 || !ref0 || arePoligon(e0, ref0)) return G;
  const brRef = c.brat == null ? null
    : (ref0.brate ?? []).find((x) => Math.abs(x.la_mm - (c.la_mm ?? NaN)) <= 1) ?? null;
  if (c.brat != null && !brRef) return G;
  const e = normalizat(e0);
  const creste = (poz - c.poz) * c.sens > 0;
  const sec: Record<string, SectiuneEsv> = {};
  for (const [k, s] of Object.entries(e.sectiune_per_nivel)) {
    const n = Number(k);
    const cat: Cat | null = nivel == null || n === nivel ? "exact"
      : creste && n < nivel ? "cel_putin" : !creste && n > nivel ? "cel_mult" : null;
    sec[k] = cat == null || arePoligon(e, s) ? s
      : brRef ? cuBratul(e, s, brRef, c.sens, poz, cat, creste)
      : cuPiciorul(e, s, c.sens, poz, cat);
  }
  const G1: Geometrie = { ...G, esv: G.esv.map((x) => (x.id === id ? { ...e, sectiune_per_nivel: sec } : x)) };
  return grinzileUrmeaza(G1, G, id, c);
}

/** Lungimea piesei cu fața `c` la `poz`. */
export const lungimeaPiesei = (c: Capat, poz: number = c.poz): number => (poz - c.fix) * c.sens;

// --- unirea ---------------------------------------------------------------------

export type Unire =
  | { G: Geometrie; id: string; inghitit: string; grinzi: string[] }
  | { eroare: "forma" | "niveluri" | "plantat" | "amprenta"; esv?: string };

const areBrate = (e: Esv): boolean =>
  Object.values(e.sectiune_per_nivel).some((s) => (s.brate ?? [])
    .some((b) => b.L_cm > LATURA_MIN_CM + 1e-6));

/** Secțiunea crește în sus undeva (`tipuri.valideaza`: latura lungă, grosimea,
 *  suma brațelor)? */
function cresteInSus(e: Esv): boolean {
  const niv = nivelurileEsv(e);
  return niv.slice(1).some((n, i) => {
    const jos = e.sectiune_per_nivel[String(niv[i])];
    const sus = e.sectiune_per_nivel[String(n)];
    const suma = (s: SectiuneEsv) => (s.brate ?? []).reduce((t, b) => t + b.L_cm, 0);
    return sus.L_cm > jos.L_cm + 1e-6 || sus.b_cm > jos.b_cm + 1e-6 || suma(sus) > suma(jos) + 1e-6;
  });
}

const aceeasiBaza = (a: Esv, b: Esv) =>
  JSON.stringify(a.baza) === JSON.stringify(b.baza)
  && nivelurileEsv(a)[0] === nivelurileEsv(b)[0];

/** Brațul `x` adăugat secțiunii: la locul altui braț (de partea cealaltă a
 *  piciorului) se unesc într-unul singur, care trece prin picior. */
function cuBratNou(lista: Brat[], x: Brat): Brat[] {
  const i = lista.findIndex((y) => Math.abs(y.la_mm - x.la_mm) < Math.max(x.b_cm, y.b_cm) * 5);
  if (i < 0) return [...lista, x];
  return lista.map((y, j) => {
    if (j !== i) return y;
    const lo = Math.min(y.ofs_mm - y.L_cm * 5, x.ofs_mm - x.L_cm * 5);
    const hi = Math.max(y.ofs_mm + y.L_cm * 5, x.ofs_mm + x.L_cm * 5);
    return { ...y, b_cm: Math.max(y.b_cm, x.b_cm), L_cm: r1((hi - lo) / 10), ofs_mm: r1((lo + hi) / 2) };
  });
}

/** Piciorul lui `e` întins pe axa lui până cuprinde [lo, hi]. */
function cuprinde(e: Esv, s: SectiuneEsv, lo: number, hi: number): SectiuneEsv {
  const f = forma(e, s);
  const A0 = Math.min(f.A0, lo);
  const A1 = Math.max(f.A1, hi);
  return faraAmprenta({ ...s, L_cm: r1((A1 - A0) / 10), ofs_mm: r1((A0 + A1) / 2 - e.nod[f.lg]) });
}

/** DOUĂ PICIOARE ÎN PRELUNGIRE: `B` intră în piciorul lui `A`, cu brațele
 *  lui la locul lor. Grosimea cuprinde amândouă benzile, centrată pe axa lui
 *  `A`. */
function prelungire(A: Esv, B: Esv): Record<string, SectiuneEsv> {
  const { lg, tr } = indici(A);
  const dLa = B.nod[lg] - A.nod[lg];
  const dOfs = B.nod[tr] - A.nod[tr];
  const out: Record<string, SectiuneEsv> = {};
  for (const n of new Set([...nivelurileEsv(A), ...nivelurileEsv(B)])) {
    const sA = sectiunea(A, n);
    const sB = sectiunea(B, n);
    const fe = [sA && forma(A, sA), sB && forma(B, sB)].filter((x): x is Forma => !!x);
    const A0 = Math.min(...fe.map((f) => f.A0));
    const A1 = Math.max(...fe.map((f) => f.A1));
    const jum = Math.max(...fe.map((f) => Math.max(A.nod[tr] - f.bLo, f.bHi - A.nod[tr])));
    const brate = [...(sA?.brate ?? []),
      ...(sB?.brate ?? []).map((x) => ({ ...x, la_mm: r1(x.la_mm + dLa), ofs_mm: r1(x.ofs_mm + dOfs) }))];
    const sec: SectiuneEsv = { b_cm: r1((2 * jum) / 10), L_cm: r1((A1 - A0) / 10),
                               ofs_mm: r1((A0 + A1) / 2 - A.nod[lg]) };
    out[String(n)] = brate.length ? { ...sec, brate } : sec;
  }
  return out;
}

/** UN ELEMENT FĂRĂ BRAȚE DEVINE BRAȚUL ALTUIA: piciorul lui `X` (pe axa
 *  cealaltă) e brațul lui `S`, la locul lui, pornit din fața de departe a
 *  piciorului lui `S`; piciorul lui `S` cuprinde îmbinarea. */
function caBrat(S: Esv, X: Esv): Record<string, SectiuneEsv> {
  const out: Record<string, SectiuneEsv> = {};
  for (const n of nivelurileEsv(S)) {
    const sS = sectiunea(S, n)!;
    const sX = sectiunea(X, n);
    if (!sX) { out[String(n)] = sS; continue; }
    const fS = forma(S, sS);
    const fX = forma(X, sX);
    // piciorul lui X: de-a lungul lui (axa `tr` a lui S) [A0, A1], banda lui
    // pe axa lui S [bLo, bHi]
    let [t0, t1] = [fX.A0, fX.A1];
    if (t0 >= fS.bHi - EPS) t0 = Math.min(t0, fS.bLo);
    else if (t1 <= fS.bLo + EPS) t1 = Math.max(t1, fS.bHi);
    const brat: Brat = { la_mm: r1((fX.bLo + fX.bHi) / 2 - S.nod[fS.lg]), b_cm: r1((fX.bHi - fX.bLo) / 10),
                         L_cm: r1((t1 - t0) / 10), ofs_mm: r1((t0 + t1) / 2 - S.nod[fS.tr]) };
    const s = cuprinde(S, sS, fX.bLo, fX.bHi);
    out[String(n)] = { ...s, brate: cuBratNou(s.brate ?? [], brat) };
  }
  return out;
}

/** BRAȚUL PRELUNGIT PESTE UN ELEMENT FĂRĂ BRAȚE: brațul de la `la_mm` al lui
 *  `A` cuprinde piciorul lui `B` (pe axa brațului), lat cât amândouă. */
function bratPrelungit(A: Esv, la_mm: number, B: Esv): Record<string, SectiuneEsv> | null {
  const out: Record<string, SectiuneEsv> = {};
  for (const n of nivelurileEsv(A)) {
    const sA = sectiunea(A, n)!;
    const sB = sectiunea(B, n);
    if (!sB) { out[String(n)] = sA; continue; }
    const lista = sA.brate ?? [];
    const i = lista.findIndex((x) => Math.abs(x.la_mm - la_mm) <= 1);
    if (i < 0) return null;
    const fA = forma(A, sA);
    const fB = forma(B, sB);
    const x = lista[i];
    const cLa = A.nod[fA.lg] + x.la_mm;
    const t0 = Math.min(A.nod[fA.tr] + x.ofs_mm - x.L_cm * 5, fB.A0);
    const t1 = Math.max(A.nod[fA.tr] + x.ofs_mm + x.L_cm * 5, fB.A1);
    const jum = Math.max(x.b_cm * 5, cLa - fB.bLo, fB.bHi - cLa);
    const nou: Brat = { ...x, b_cm: r1((2 * jum) / 10), L_cm: r1((t1 - t0) / 10),
                        ofs_mm: r1((t0 + t1) / 2 - A.nod[fA.tr]) };
    const s = { ...sA, brate: lista.map((y, j) => (j === i ? nou : y)) };
    out[String(n)] = cuPiciorulIntreg(A, cuprinde(A, s, cLa - jum, cLa + jum));
  }
  return out;
}

/** ESV-UL `idA`, A CĂRUI FAȚĂ `c` A AJUNS LA `idB`, SE UNEȘTE CU EL.
 *
 *  Rămâne un singur element, pe toate nivelurile lor: două picioare în
 *  prelungire devin unul; un element fără brațe, pe axa cealaltă, devine
 *  brațul celuilalt (întâi lovit — `B` —, ca bara unui T să rămână
 *  întreagă); un braț tras într-un element fără brațe se prelungește peste el.
 *  Restul nu încape în schemă (un braț din braț) și se refuză.
 *
 *  Grinzile dintre cele două pleacă; cele ale elementului înghițit trec la cel
 *  rămas, cu capetele în același punct. Elementele trebuie să pornească de pe
 *  aceeași bază, iar unul înghițit ca braț nu poate urca mai sus decât cel
 *  care rămâne. Nimic nu poate sta pe o grindă care pleacă sau pe elementul
 *  înghițit. */
export function unesteEsv(G: Geometrie, idA: string, idB: string, c: Capat): Unire {
  const A0 = G.esv.find((x) => x.id === idA);
  const B0 = G.esv.find((x) => x.id === idB);
  if (!A0 || !B0 || idA === idB) return { eroare: "forma" };
  const cuPoligon = (e: Esv) => Object.values(e.sectiune_per_nivel).some((s) => arePoligon(e, s));
  if (cuPoligon(A0) || cuPoligon(B0)) return { eroare: "amprenta" };
  if (!aceeasiBaza(A0, B0)) return { eroare: "niveluri" };
  const A = normalizat(A0);
  const B = normalizat(B0);
  const subset = (x: Esv, y: Esv) => nivelurileEsv(x).every((n) => !!sectiunea(y, n));

  let S: Esv, X: Esv, sec: Record<string, SectiuneEsv> | null;
  if (c.brat == null && B.axa === A.axa) {
    // pe aceeași linie: benzile picioarelor se privesc pe fiecare nivel comun
    // (o față lipită de brațul unui element paralel nu e o prelungire)
    const pe = nivelurileEsv(A).filter((n) => sectiunea(B, n)).every((n) => {
      const fa = forma(A, sectiunea(A, n)!);
      const fb = forma(B, sectiunea(B, n)!);
      return Math.min(fa.bHi, fb.bHi) - Math.max(fa.bLo, fb.bLo) > EPS;
    });
    if (!pe) return { eroare: "forma" };
    [S, X, sec] = [A, B, prelungire(A, B)];
  } else if (c.brat == null && !areBrate(A) && subset(A, B)) {
    [S, X, sec] = [B, A, caBrat(B, A)];
  } else if (c.brat == null && !areBrate(B) && subset(B, A)) {
    [S, X, sec] = [A, B, caBrat(A, B)];
  } else if (c.brat != null && B.axa !== A.axa && !areBrate(B)) {
    [S, X, sec] = [A, B, bratPrelungit(A, c.la_mm ?? NaN, B)];
  } else if (c.brat == null && (!areBrate(A) || !areBrate(B))) {
    return { eroare: "niveluri" };
  } else {
    return { eroare: "forma" };
  }
  if (!sec) return { eroare: "niveluri" };

  const intre = G.grinzi.filter((g) => (g.nod_i === S.id && g.nod_j === X.id)
    || (g.nod_i === X.id && g.nod_j === S.id)).map((g) => g.id);
  const plecate = new Set(intre);
  const plantat = G.esv.find((e) => typeof e.baza === "object" && e.baza
    && (plecate.has((e.baza as { grinda?: string }).grinda ?? "")
      || (e.baza as { element?: string }).element === X.id));
  if (plantat) return { eroare: "plantat", esv: plantat.id };

  const unit: Esv = { ...S, sectiune_per_nivel: sec,
                      pereche: S.pereche === X.id ? null : S.pereche };
  if (cresteInSus(unit)) return { eroare: "niveluri" };
  const curat = (o: Pt): Pt | null =>
    (Math.abs(o[0]) < 1e-6 && Math.abs(o[1]) < 1e-6 ? null : [r1(o[0]), r1(o[1])]);
  const muta = (o: Pt | null | undefined): Pt | null => {
    const v = o ?? [0, 0];
    return curat([X.nod[0] + v[0] - S.nod[0], X.nod[1] + v[1] - S.nod[1]]);
  };
  const grinzi = G.grinzi.filter((g) => !plecate.has(g.id)).map((g) => {
    if (g.nod_i !== X.id && g.nod_j !== X.id) return g;
    return {
      ...g,
      ...(g.nod_i === X.id ? { nod_i: S.id, ofs_i: muta(g.ofs_i) } : {}),
      ...(g.nod_j === X.id ? { nod_j: S.id, ofs_j: muta(g.ofs_j) } : {}),
    };
  });
  const esv = G.esv.filter((e) => e.id !== X.id).map((e) => (e.id === S.id ? unit
    : e.pereche === X.id ? { ...e, pereche: null } : e));
  const transfer = G.grinzi_transfer?.filter((g) => !plecate.has(g));
  return {
    G: { ...G, esv, grinzi, ...(transfer ? { grinzi_transfer: transfer } : {}) },
    id: S.id, inghitit: X.id, grinzi: intre,
  };
}
